/**
 * "Did this request fail to reach the server?" — the portal's ONE predicate
 * (CU-86akbg4qm).
 *
 * Shared by the SMS login challenge (`useMfaChallenge`), SMS enrollment
 * (`useSmsEnrollment`) and the passkey flows (`services/passkey/networkError.ts`
 * re-exports it). It lives in one module so there is never a second copy: two
 * divergent classifiers on the same ceremony is how one drifts — the reason
 * mobile's `services/mfa/networkError.ts` exists as a single module too.
 *
 * ── NEVER PREDICT CONNECTIVITY ────────────────────────────────────────────
 *
 * Mobile considered and REJECTED NetInfo (2026-08-05): `isConnected` is true on
 * a captive portal, and a preemptive banner implies the MFA gate exists
 * BECAUSE the patient is offline. The same holds here. This classifies a
 * request that ACTUALLY failed. `navigator.onLine` is read below only to
 * CORROBORATE such a failure, never before a request and never to show a
 * banner.
 *
 * ⚠️ EVERY TRANSPORT SIGNAL NEEDS THAT CORROBORATION — adversarial review,
 * 2026-09-28. The first version exempted `auth/network-request-failed` as
 * "unambiguous". It is not: only the OFFLINE direction had been measured. The
 * reviewer ran the real `@firebase/auth` 1.10.8 against a stubbed `fetch`: a
 * REACHED server returning an HTML 503 yields `auth/network-request-failed`
 * (the catch at `index-35c79a8a.js:965-972` maps ANY non-Firebase error,
 * including `response.json()` failing on a non-JSON body), and so does the
 * SDK's own 30s/60s timeout against a hung server. Without corroboration an
 * IdP or SMS-vendor outage on an ONLINE browser was classified offline, held,
 * never flushed (no `online` event, no success) — zero events from every
 * patient, each told to check their connection. That is the enrollment
 * hook's own "must not hide" case.
 *
 * ── WHAT IS MEASURED (docs/projects/CU-86akbg4qm/…_PROBE_FINDINGS.md) ─────
 *
 *   | leg                                  | offline code                    |
 *   |--------------------------------------|---------------------------------|
 *   | challenge send (reCAPTCHA config)    | auth/network-request-failed ✅  |
 *   | challenge resolve (mfaSignIn:finalize)| auth/network-request-failed ✅ |
 *   | enrollment mint (httpsCallable)      | functions/internal, msg "internal" ✅ (blocked request) |
 *   | reCAPTCHA script blocked, ONLINE     | auth/internal-error — NOT offline ✅ (probe 3) |
 *   | enrollment FIRST send, truly offline | auth/internal-error — offline ✅ (staging, 2026-09-28) |
 *
 * ⚠️ BOTH SDKs ASSIGN THE CODE THEMSELVES, in every browser. `@firebase/auth`
 * turns ANY `fetch` rejection into `auth/network-request-failed`
 * (`_performFetchWithErrorHandling`); `@firebase/functions` turns it into
 * `status: 0` → `functions/internal` and DISCARDS the browser's prose. So the
 * browser only ever changes prose we mostly do not see — Safari and Chrome
 * behaved identically in every observable during probing.
 *
 * ⚠️ DELIBERATELY NARROW. Anything unrecognised MUST fall through to the
 * caller's generic handling, which still shows friendly copy and still
 * captures to Sentry. Widening this tells a patient with perfect signal to
 * check their connection AND silences the capture that is our only evidence a
 * real fault exists.
 */

/**
 * ⚠️ AMBIGUOUS, like everything below — offline ONLY with `onLine === false`.
 *
 * `auth/network-request-failed`: MEASURED offline on the challenge send and
 * resolve legs (2026-09-24) — and, per the adversarial review, ALSO produced
 * by a reached server's non-JSON 5xx and by the SDK's own timeout.
 *
 * `auth/internal-error`: MEASURED OFFLINE on staging release v2.46.2
 * (2026-09-28 17:16:25 UTC, `enroll-send-otp`): the enrollment gate's FIRST
 * send, genuinely offline, before reCAPTCHA had ever loaded. Loading reCAPTCHA
 * fails and the SDK reports its catch-all code; the retry, with reCAPTCHA now
 * loaded, gave `network-request-failed`. Before this was listed, that first
 * offline send showed "try again in a moment" — it surfaced only through the
 * telemetry-only hint. ⚠️ It was deliberately EXCLUDED at first because of
 * probe 3 (reCAPTCHA blocked while ONLINE — reconnecting will not help). That
 * case is still excluded, by the corroboration below rather than by the list.
 * A server cannot answer a browser with no network, so an `internal-error`
 * while offline is not a server fault.
 */
const AUTH_TRANSPORT_CODES = new Set(['auth/network-request-failed', 'auth/internal-error']);

/**
 * ⚠️ A transport failure and a SERVER FAULT share these codes.
 *
 * `@firebase/functions` maps a failed `fetch` to HTTP status 0 → `internal`,
 * and its own comment on that branch reads "This can happen if the server
 * returns 500". A genuine 500/503 therefore arrives as the SAME
 * `functions/internal` / `functions/unavailable` an offline patient produces —
 * and the message is `internal` either way, so no prose can separate them.
 * MEASURED 2026-09-28 (probe 4b): a blocked `mfaEnrollAuthorization` call gave
 * `functions/internal`, message `internal`, in 32 µs.
 *
 * So these — like every signal here — count as offline ONLY when the browser
 * also reports no network at the moment of the failure. A server response
 * cannot arrive while the browser has no network, so this cannot mis-read a
 * genuine 500 as offline.
 * A blocked request, an extension or a captive portal keep `onLine === true`
 * and stay generic — correctly, since reconnecting would not help them.
 *
 * ⚠️ WHY NOT LATENCY. 32 µs blocked against ~1.3 s served looks decisive, but
 * client code measures SDK call → rejection (SDK overhead included), and a
 * patient near `us-central1` on a warm connection can get a real 500 back in
 * tens of milliseconds. A threshold safe for them is fragile.
 *
 * ⚠️ THIS CHANGED PASSKEY BEHAVIOUR (approved 2026-09-28). The passkey
 * predicate used to treat these codes as network unconditionally, so a real
 * passkey server fault was told "reconnect" and only breadcrumbed. It is now
 * `failed` and captured again.
 */
const FUNCTIONS_TRANSPORT_CODES = new Set([
  'functions/internal',
  // Inferred, not measured: documented transport-side failures of the callable
  // SDK. Held to the same corroboration rule, since a server can emit both.
  'functions/unavailable',
  'functions/deadline-exceeded',
]);

/**
 * Prose fallback, for a CODELESS failure only — and, like the codes, only
 * while the browser reports no network: a CORS-less 5xx also surfaces as a
 * bare "Failed to fetch".
 *
 * ⚠️ INFERRED, NOT MEASURED ON THIS PLATFORM. No leg probed on 2026-09-24/28
 * arrived without a code — both SDKs assign one. This remains because mobile
 * MEASURED a codeless transport failure (passkey leg, 2026-08-06), so no site
 * may rely on `code` alone. The phrasings are each engine's documented
 * `fetch` rejection text.
 *
 * Matched narrowly and case-insensitively — deliberately NOT a loose
 * `/network/` test, since server prose can say "network" without any
 * connectivity problem existing.
 */
const NETWORK_MESSAGE_PATTERNS = [
  // Chromium
  'failed to fetch',
  // Firefox
  'networkerror when attempting to fetch resource',
  // Safari / WebKit
  'the network connection was lost',
  'load failed',
  // Cross-browser
  'network request failed',
  'internet connection appears to be offline',
];

function messageLooksOffline(message: string): boolean {
  if (!message) return false;
  const haystack = message.toLowerCase();
  return NETWORK_MESSAGE_PATTERNS.some((p) => haystack.includes(p));
}

/**
 * The browser's own view, read at the moment of a failure. `true` when there
 * is no `navigator` (SSR, some test hosts): assuming ONLINE is the fail-safe
 * direction, because it can only withhold the offline label, never invent it.
 */
export function browserReportsOnline(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') return true;
  return navigator.onLine;
}

/**
 * True when the failure means the request never reached the server.
 *
 * @param code    - the SDK error code, or "" when absent
 * @param message - optional error prose. ⚠️ PASS IT WHERE YOU HAVE IT: without
 *                  it, a codeless transport failure is invisible here.
 * @param online  - the browser's connectivity at failure time. Defaults to
 *                  `navigator.onLine`; a parameter so tests can drive both
 *                  sides without stubbing a global.
 */
export function isNetworkError(code: string, message?: string, online: boolean = browserReportsOnline()): boolean {
  // ⚠️ FIRST, and for every signal. While the browser has a network, each code
  // and phrase below is ALSO what a reachable-but-failing server produces, and
  // calling that "offline" silences the capture that would reveal an outage.
  // The cost of this line is only ever showing today's fallback copy.
  if (online) return false;
  if (AUTH_TRANSPORT_CODES.has(code) || FUNCTIONS_TRANSPORT_CODES.has(code)) return true;
  // A code we did not list is a SERVER answer, however it is phrased — only a
  // codeless failure may be classified from prose.
  if (code) return false;
  return messageLooksOffline(message ?? '');
}
